import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import progressGif from "../../images/progress.gif";

const Pmgkay_FPS = ({ distCode, distName, month, year, singleRowOnly = false }) => {
  const [data, setData] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);

  const headerStyle = {
    backgroundColor: "black",
    color: "white",
    textAlign: "center",
    padding: "3px 5px",
    fontSize: "14px",
  };

  const cellStyle = {
    padding: "3px 5px",
    textAlign: "center",
    fontSize: "14px",
  };

  const totalStyle = {
    backgroundColor: "black",
    color: "white",
    textAlign: "center",
    fontWeight: "bold",
    padding: "3px 5px",
    fontSize: "14px",
  };

  const getMonthName = (monthNumber) => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return monthNames[monthNumber - 1] || "";
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!distCode) return;
      setLoading(true);
      try {
        const schemeRes = await axios.get(`${process.env.REACT_APP_URL_SIDE}/api/pmgkay/schemes`);
        setSchemes(schemeRes.data);

        const summaryRes = await axios.post(`${process.env.REACT_APP_URL_SIDE}/api/pmgkay/fpsDetails`, {
          month,
          year,
          afsoCode: distCode, 
          schemeIds: schemeRes.data.map((s) => s.schemeId),
          singleRowOnly,
        });

        setData(summaryRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [distCode, month, year, singleRowOnly]);

  const handleDownload = () => {
    if (!data.length) return;

    const sheetData = [];
    sheetData.push([`${distName} PMGKAY Details for ${getMonthName(Number(month))} '${year}`]);
    sheetData.push([]);

  const headers = ["Sl.No", "FPS Id", "Total Cards"];
schemes.forEach((s) =>
  headers.push(`${s.schemeDesc} Cards`, `${s.schemeDesc} Rice Sale`, `${s.schemeDesc} Wheat Sale`)
);
headers.push("Avail PMGKAY Cards", "Availed %", "Total Rice", "Total Wheat");
sheetData.push(headers);

data.forEach((row, idx) => {
  const rowData = [
    idx + 1,
    row.fpsId || "-", 
    row.totalCards ?? 0,
  ];
  schemes.forEach((s) => {
    rowData.push(
      row.schemeCards[s.schemeId] ?? 0,
      (row.riceSale[s.schemeId] ?? 0).toFixed(3),
      (row.wheatSale[s.schemeId] ?? 0).toFixed(3)
    );
  });
  rowData.push(
    row.availPmgkayCards ?? 0,
    row.totalCards ? ((row.availPmgkayCards / row.totalCards) * 100).toFixed(2) : "0.00",
    (row.pmgkaySale ?? 0).toFixed(3),
    (row.pmgkayWheatSale ?? 0).toFixed(3)
  );
  sheetData.push(rowData);
});


    const totalsRow = [
      "Total", "", data.reduce((sum, row) => sum + (row.totalCards || 0), 0)
    ];
    schemes.forEach((s) => {
      totalsRow.push(
        data.reduce((sum, row) => sum + (row.schemeCards[s.schemeId] || 0), 0),
        data.reduce((sum, row) => sum + (row.riceSale[s.schemeId] || 0), 0).toFixed(3),
        data.reduce((sum, row) => sum + (row.wheatSale[s.schemeId] || 0), 0).toFixed(3)
      );
    });
    totalsRow.push(
      data.reduce((sum, row) => sum + (row.availPmgkayCards || 0), 0),
      (data.reduce((sum, row) => sum + (row.availPmgkayCards || 0), 0) / data.reduce((sum, row) => sum + (row.totalCards || 0), 0) * 100).toFixed(2),
      data.reduce((sum, row) => sum + (row.pmgkaySale || 0), 0).toFixed(3),
      data.reduce((sum, row) => sum + (row.pmgkayWheatSale || 0), 0).toFixed(3)
    );
    sheetData.push(totalsRow);

    const refreshRow = Array(headers.length).fill("");
    refreshRow[headers.length - 1] = `Refresh Time: ${new Date().toLocaleString("sv-SE", { hour12: false })}`;
    sheetData.push(refreshRow);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    XLSX.utils.book_append_sheet(wb, ws, "PMGKAY Details");
    XLSX.writeFile(wb, `PMGKAY_${distName}_${month}_${year}.xlsx`);
  };

  if (!distCode) return null;
  if (loading) return (
    <div style={{ textAlign: "center", marginTop: "20px" }}>
      <img src={progressGif} alt="Loading..." width="250" height="15" />
    </div>
  );

  return (
    <div style={{ paddingBottom: "300px" }}>
      <table border="1" cellPadding="5" cellSpacing="0" width="70%" align="center">
        <thead>
          <tr>
            <th colSpan={3 + schemes.length * 3 + 4} style={{ ...headerStyle, fontSize: "18px", padding: "10px" }}>
              {distName} PMGKAY Details for {getMonthName(Number(month))} '{year}
            </th>
          </tr>
          <tr>
            <th rowSpan="2" style={headerStyle}>Sl.No</th>
            <th rowSpan="2" style={headerStyle}>FPS Id</th>
            <th rowSpan="2" style={headerStyle}>Total<br/>Cards</th>
            {schemes.map((s) => (
              <th key={s.schemeId} colSpan="3" style={headerStyle}>{s.schemeDesc}</th>
            ))}
            <th colSpan="4" style={headerStyle}>Total</th>
          </tr>
          <tr>
            {schemes.map((s) => (
              <>
                <th style={headerStyle}>Cards</th>
                <th style={headerStyle}>Rice Sale</th>
                <th style={headerStyle}>Wheat Sale</th>
              </>
            ))}
            <th style={headerStyle}>Avail Cards</th>
            <th style={headerStyle}>Availed %</th>
            <th style={headerStyle}>Rice Sale</th>
            <th style={headerStyle}>Wheat Sale</th>
          </tr>
        </thead>
        <tbody>
  {data.map((row, idx) => (
    <tr key={idx}>
      <td style={cellStyle}>{idx + 1}</td>
      <td style={cellStyle}>{row.fpsId || "-"}</td> 
      <td style={cellStyle}>{row.totalCards ?? 0}</td>
      {schemes.map((s) => (
        <>
          <td style={cellStyle}>{row.schemeCards[s.schemeId] ?? 0}</td>
          <td style={cellStyle}>{(row.riceSale[s.schemeId] ?? 0).toFixed(3)}</td>
          <td style={cellStyle}>{(row.wheatSale[s.schemeId] ?? 0).toFixed(3)}</td>
        </>
      ))}
      <td style={cellStyle}>{row.availPmgkayCards ?? 0}</td>
      <td style={cellStyle}>{row.totalCards ? ((row.availPmgkayCards / row.totalCards) * 100).toFixed(2) : "0.00"}</td>
      <td style={cellStyle}>{(row.pmgkaySale ?? 0).toFixed(3)}</td>
      <td style={cellStyle}>{(row.pmgkayWheatSale ?? 0).toFixed(3)}</td>
    </tr>
  ))}
</tbody>

      </table>

      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={handleDownload}
          style={{
            padding: "6px 20px",
            fontSize: "14px",
            backgroundColor: "black",
            color: "white",
            border: "none",
            borderRadius: "20px",
            cursor: "pointer"
          }}
        >
          Data to Excel
        </button>
      </div>
    </div>
  );
};

export default Pmgkay_FPS;
